import type * as Monaco from "monaco-editor";
import { ECodeInsertionMode, IPlannedCodeInsertion, planCodeInsertion } from "./ai-code-insertion";
import { MonacoPendingEdit } from "./monaco-pending-edit";

/**
 * Host-side controller for "show it before you write it". Every code editor that offers the
 * AI panel holds one of these: it plans the edit, keeps the preview on screen, and hands the
 * editor's new value back once the user confirms.
 */
export interface IAiPendingEditOptions {
  /** The plan on screen, or null once it is applied, discarded or invalidated. */
  onChange: (plan: IPlannedCodeInsertion | null) => void;
}

export class AiPendingEdit {
  private readonly options: IAiPendingEditOptions;

  private preview: MonacoPendingEdit | null = null;

  private editor: Monaco.editor.IStandaloneCodeEditor | null = null;

  /** The last code block the user pressed Apply on, so the mode can be switched without it. */
  private snippet: string = "";

  constructor(options: IAiPendingEditOptions) {
    this.options = options;
  }

  /** Call from the editor's onMount. */
  attach(monacoInstance: typeof Monaco, editor: Monaco.editor.IStandaloneCodeEditor): void {
    this.preview?.dispose();
    this.editor = editor;
    this.preview = new MonacoPendingEdit(monacoInstance, editor, {
      onInvalidated: (): void => this.options.onChange(null)
    });
  }

  /** The user pressed Apply on a code block: work out what it would do and draw that. */
  request = (code: string): void => {
    this.snippet = code;
    this.show("at-cursor");
  };

  /** The user chose the other landing spot for the same code block. */
  setMode = (mode: ECodeInsertionMode): void => {
    this.show(mode);
  };

  /** Writes the pending edit and returns the editor's new value, or null when nothing was pending. */
  commit(source: string = "ai-apply"): string | null {
    const value: string | null = this.preview?.commit(source) ?? null;
    this.options.onChange(null);
    return value;
  }

  discard = (): void => {
    this.preview?.clear();
    this.options.onChange(null);
  };

  dispose(): void {
    this.preview?.dispose();
    this.preview = null;
    this.editor = null;
  }

  private show = (mode: ECodeInsertionMode): void => {
    const editor: Monaco.editor.IStandaloneCodeEditor | null = this.editor;
    const model: Monaco.editor.ITextModel | null | undefined = editor?.getModel();
    if (!editor || !model || !this.preview) return;

    const selection: Monaco.Selection | null = editor.getSelection();
    const plan: IPlannedCodeInsertion | null = planCodeInsertion(
      {
        lines: model.getLinesContent(),
        position: editor.getPosition() ?? { lineNumber: 1, column: 1 },
        selection: selection && !selection.isEmpty() ? selection : null,
        snippet: this.snippet,
        eol: model.getEOL()
      },
      mode
    );
    if (!plan) return;

    this.preview.show(plan);
    this.options.onChange(plan);
  };
}
